import React, { useState, useContext, useEffect, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { IcUsers, IcExcel, FaIcon } from "../../Icons";
import axios from "axios";
import Header from "../../layout/Header/Header";
import { Input, Spinner } from "reactstrap";
import ExportJsonExcel from "js-export-excel";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LabelList, Cell,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────
const COLOR      = "#8b5cf6";
const PIE_COLORS = ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#0ea5e9", "#6366f1", "#ec4899", "#14b8a6"];
const fmt        = (v) => "$" + new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(v || 0);

// Orden cronológico de meses (para ordenar de más antiguo a más nuevo)
const MES_ORDEN = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6,
  julio: 7, agosto: 8, septiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};
const mesNum = (m) => MES_ORDEN[(m || "").toString().trim().toLowerCase()] || 99;

// Columnas del PDF "Clientes Activos"
//   Encargado = contacto · Renta = produccion
const COLUMNS = [
  { key: "nombre_comercial", label: "Clientes" },
  { key: "contacto",         label: "Encargado" },
  { key: "ramo",             label: "Ramo" },
  { key: "mes",              label: "Mes" },
  { key: "produccion",       label: "Renta" },
  { key: "anio",             label: "Año" },
  { key: "montoRenta",       label: "Monto Renta", money: true },
];

function ChartTooltip({ active, payload, label, suffix }) {
  if (!active || !payload?.length) return null;
  const v = payload[0]?.value;
  return (
    <div style={{
      background: "#0f172a", borderRadius: 10, padding: "10px 16px",
      boxShadow: "0 8px 24px rgba(0,0,0,0.25)", fontSize: "0.78rem",
    }}>
      <div style={{ color: "#94a3b8", fontWeight: 700, marginBottom: 4, fontSize: "0.72rem" }}>{label}</div>
      <div style={{ color: "#fff", fontWeight: 800, fontSize: "0.92rem" }}>
        {v} cliente{v === 1 ? "" : "s"}{suffix || ""}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function ClientesActivos() {
  const { user }     = useContext(AuthContext);
  const URL_CLIENTES      = process.env.REACT_APP_URL_CLIENTES;
  const URL_MENSUALIDADES = process.env.REACT_APP_URL_MENSUALIDADES;

  const [clientes,    setClientes]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [rentaFilter, setRentaFilter] = useState(""); // "", "Si", "No"
  const [anioFilter,  setAnioFilter]  = useState(""); // "" = todos

  useEffect(() => {
    setLoading(true);
    const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };
    Promise.all([
      axios.get(URL_CLIENTES, { headers }),
      axios.get(URL_MENSUALIDADES, { headers }),
    ])
      .then(([cliRes, mensRes]) => {
        const cli  = cliRes.data || [];
        const mens = mensRes.data || [];
        // Monto de renta por cliente = suma de importe_actual de TODAS sus
        // mensualidades (incluye las inactivas, para que también muestren monto)
        const rentaPorCliente = {};
        mens.forEach((m) => {
          (m.clientes || []).forEach((c) => {
            const id = c && (c._id || c);
            if (!id) return;
            rentaPorCliente[id] = (rentaPorCliente[id] || 0) + (Number(m.importe_actual) || 0);
          });
        });
        // Enriquecer cada cliente con montoRenta
        const enriched = cli.map((c) => ({ ...c, montoRenta: rentaPorCliente[c._id] || 0 }));
        setClientes(enriched);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [URL_CLIENTES, URL_MENSUALIDADES]);

  // Años disponibles (para el filtro)
  const aniosDisponibles = useMemo(() => {
    const set = new Set();
    clientes.filter((c) => c.is_active === "Si").forEach((c) => {
      const a = (c.anio || "").toString().trim();
      if (a) set.add(a);
    });
    return [...set].sort();
  }, [clientes]);

  // Clientes activos filtrados por búsqueda, renta (produccion) y año
  const activos = useMemo(() => {
    let base = clientes.filter((c) => c.is_active === "Si");
    if (rentaFilter) {
      base = base.filter((c) => (c.produccion || "").toString().trim().toLowerCase() === rentaFilter.toLowerCase());
    }
    if (anioFilter) {
      base = base.filter((c) => (c.anio || "").toString().trim() === anioFilter);
    }
    const s = search.trim().toLowerCase();
    if (!s) return base;
    return base.filter((c) =>
      COLUMNS.some((col) => (c[col.key] ?? "").toString().toLowerCase().includes(s))
    );
  }, [clientes, search, rentaFilter, anioFilter]);

  const data = useMemo(() => {
    if (!activos.length) return null;

    const porAnioMap = activos.reduce((acc, c) => {
      const a = (c.anio || "").toString().trim();
      if (!a) return acc;
      acc[a] = (acc[a] || 0) + 1;
      return acc;
    }, {});
    const porAnio = Object.keys(porAnioMap).sort().map((a) => ({ anio: a, clientes: porAnioMap[a] }));

    const porRamoMap = activos.reduce((acc, c) => {
      const r = (c.ramo || "Sin ramo").toString().trim() || "Sin ramo";
      acc[r] = (acc[r] || 0) + 1;
      return acc;
    }, {});
    const porRamo = Object.keys(porRamoMap)
      .map((r) => ({ ramo: r, clientes: porRamoMap[r] }))
      .sort((a, b) => b.clientes - a.clientes);

    const conRenta = activos.filter((c) => (c.produccion || "").toString().trim().toLowerCase() === "si").length;
    const anioTop  = porAnio.reduce((max, x) => (x.clientes > (max?.clientes || 0) ? x : max), null);
    const ramoTop  = porRamo[0];

    return { total: activos.length, conRenta, anioTop, ramoTop, porAnio, porRamo };
  }, [activos]);

  // Filas ordenadas por año y mes (más antiguo a más nuevo), luego nombre
  const filas = useMemo(() =>
    [...activos].sort((a, b) => {
      const ay = parseInt(a.anio, 10) || 9999;
      const by = parseInt(b.anio, 10) || 9999;
      if (ay !== by) return ay - by;
      const am = mesNum(a.mes);
      const bm = mesNum(b.mes);
      if (am !== bm) return am - bm;
      return (a.nombre_comercial || "").localeCompare(b.nombre_comercial || "");
    }), [activos]);

  function exportExcel() {
    const sheetData = filas.map((c) => {
      const row = {};
      COLUMNS.forEach((col) => {
        row[col.label] = col.money ? (c[col.key] || 0) : (c[col.key] ?? "");
      });
      return row;
    });
    const headers = COLUMNS.map((c) => c.label);
    const toExcel = new ExportJsonExcel({
      fileName: "ClientesActivos",
      datas: [{ sheetData, sheetName: "Clientes Activos", sheetFilter: headers, sheetHeader: headers }],
    });
    toExcel.saveExcel();
  }

  if (!user.usuarios_permisos) return null;

  const maxAnio = data?.porAnio.length ? Math.max(...data.porAnio.map((x) => x.clientes)) : 0;

  return (
    <>
      <Header />
      <div className="card container col-sm-11" style={{ paddingBottom: 32 }}>

        {/* ── Page header ──────────────────────────────────────────────── */}
        <div className="page-header">
          <div>
            <h3 className="page-title">
              <IcUsers style={{ marginRight: 8, color: COLOR }} />
              Clientes Activos
            </h3>
            <p className="page-subtitle">Listado completo de clientes activos con todos sus campos</p>
          </div>
          <div className="page-actions" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              onClick={exportExcel}
              disabled={!filas.length}
              style={{
                display: "flex", alignItems: "center", gap: 6, border: "none",
                background: "#10b981", color: "#fff", borderRadius: 8,
                padding: "7px 12px", fontSize: "0.78rem", fontWeight: 600,
                cursor: filas.length ? "pointer" : "not-allowed", opacity: filas.length ? 1 : 0.5,
              }}
            >
              <IcExcel size={15} /> Excel
            </button>
            <a href="/MenuReportes" style={{ fontSize: "0.78rem", color: "#64748b", textDecoration: "none" }}>
              ← Reportes
            </a>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
            <Spinner style={{ color: COLOR, width: "2.5rem", height: "2.5rem" }} />
            <div style={{ marginTop: 14, fontWeight: 600 }}>Cargando datos…</div>
          </div>
        ) : (
          <>
            {/* ── KPI cards ──────────────────────────────────────────────── */}
            {data && (
              <div className="row mb-3">
                {[
                  { label: "Total activos",  value: data.total,                              sub: "clientes vigentes",     icon: "fas fa-users",          color: COLOR,     bg: "#f5f3ff" },
                  { label: "Con renta",      value: data.conRenta,                           sub: "renta = Sí",            icon: "fas fa-check-circle",   color: "#f59e0b", bg: "#fffbeb" },
                  { label: "Año top",        value: data.anioTop ? data.anioTop.anio : "—",  sub: data.anioTop ? `${data.anioTop.clientes} altas` : "sin dato",     icon: "fas fa-calendar-check", color: "#10b981", bg: "#f0fdf4" },
                  { label: "Ramo principal", value: data.ramoTop ? data.ramoTop.ramo : "—",  sub: data.ramoTop ? `${data.ramoTop.clientes} clientes` : "sin dato", icon: "fas fa-tag",            color: "#3b82f6", bg: "#eff6ff" },
                ].map((card, i) => (
                  <div key={i} className="col-6 col-md-3 mb-2">
                    <div style={{ background: "#fff", borderRadius: 14, padding: "16px 18px",
                      boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderTop: `3px solid ${card.color}`,
                      display: "flex", alignItems: "center", gap: 14, height: "100%" }}>
                      <div style={{ width: 44, height: 44, borderRadius: 12, background: card.bg,
                        flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <FaIcon cls={card.icon} size={18} style={{ color: card.color }} />
                      </div>
                      <div>
                        <div style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{card.value}</div>
                        <div style={{ fontSize: "0.67rem", fontWeight: 700, color: "#64748b",
                          textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{card.label}</div>
                        <div style={{ fontSize: "0.72rem", color: card.color, fontWeight: 600, marginTop: 2 }}>{card.sub}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── Buscador + filtro de Renta ─────────────────────────────── */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>
                {filas.length} cliente{filas.length === 1 ? "" : "s"} activo{filas.length === 1 ? "" : "s"}
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Input
                  bsSize="sm"
                  type="select"
                  value={anioFilter}
                  onChange={(e) => setAnioFilter(e.target.value)}
                  style={{ maxWidth: 130 }}
                >
                  <option value="">Año: Todos</option>
                  {aniosDisponibles.map((y) => (
                    <option key={y} value={y}>{`Año: ${y}`}</option>
                  ))}
                </Input>
                <Input
                  bsSize="sm"
                  type="select"
                  value={rentaFilter}
                  onChange={(e) => setRentaFilter(e.target.value)}
                  style={{ maxWidth: 150 }}
                >
                  <option value="">Renta: Todos</option>
                  <option value="Si">Renta: Sí</option>
                  <option value="No">Renta: No</option>
                </Input>
                <Input
                  bsSize="sm"
                  type="text"
                  placeholder="Buscar en cualquier campo…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 260 }}
                />
              </div>
            </div>

            {/* ── Tabla con TODOS los campos ─────────────────────────────── */}
            <div style={{ overflowX: "auto", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 24 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem", whiteSpace: "nowrap" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    {COLUMNS.map((col) => (
                      <th key={col.key} style={{
                        padding: "8px 12px",
                        textAlign: col.money ? "right" : "left",
                        color: "#475569", fontWeight: 700,
                        fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.04em",
                        position: "sticky", top: 0,
                      }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filas.map((c, ri) => (
                    <tr key={c._id || ri} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      {COLUMNS.map((col) => {
                        const raw = c[col.key];
                        const val = col.money ? fmt(raw)
                          : (raw === undefined || raw === null || raw === "" ? "—" : raw);
                        return (
                          <td key={col.key} style={{
                            padding: "7px 12px",
                            textAlign: col.money ? "right" : "left",
                            color: col.key === "nombre_comercial" ? "#0f172a" : "#475569",
                            fontWeight: col.key === "nombre_comercial" ? 700 : 400,
                            ...(col.money && raw > 0
                              ? { color: (c.produccion || "").toString().toLowerCase() === "no" ? "#dc2626" : "#059669", fontWeight: 600 }
                              : {}),
                            ...(col.key === "produccion" && (raw || "").toString().toLowerCase() === "no"
                              ? { color: "#dc2626", fontWeight: 700 } : {}),
                          }}>{val}</td>
                        );
                      })}
                    </tr>
                  ))}
                  {filas.length === 0 && (
                    <tr>
                      <td colSpan={COLUMNS.length} style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
                        Sin clientes activos {search ? "que coincidan con la búsqueda" : ""}
                      </td>
                    </tr>
                  )}
                </tbody>
                {filas.length > 0 && (
                  <tfoot>
                    <tr style={{ background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
                      {COLUMNS.map((col, ci) => {
                        if (col.key === "montoRenta") {
                          const total = filas.reduce((s, c) => s + (Number(c.montoRenta) || 0), 0);
                          return (
                            <td key={col.key} style={{
                              padding: "9px 12px", textAlign: "right",
                              fontWeight: 800, color: "#059669", fontSize: "0.8rem",
                            }}>{fmt(total)}</td>
                          );
                        }
                        return (
                          <td key={col.key} style={{
                            padding: "9px 12px",
                            fontWeight: 800, color: "#475569",
                            fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.04em",
                          }}>{ci === 0 ? `TOTAL · ${filas.length}` : ""}</td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>

            {/* ── Gráficas (debajo de la tabla) ──────────────────────────── */}
            {data && (
              <>
                <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
                  padding: "20px 12px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", marginBottom: 20 }}>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a", padding: "0 8px 8px" }}>
                    Clientes activos por año de alta
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.porAnio} margin={{ top: 28, right: 16, left: 8, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gradCliAnio" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={COLOR} stopOpacity={0.9} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0.6} />
                        </linearGradient>
                        <linearGradient id="gradCliAnioMax" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor="#7c3aed" stopOpacity={1} />
                          <stop offset="95%" stopColor="#7c3aed" stopOpacity={0.75} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                      <XAxis dataKey="anio" tick={{ fontSize: 12, fill: "#64748b", fontWeight: 600 }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={36} />
                      <Tooltip content={<ChartTooltip />} cursor={{ fill: `${COLOR}10` }} />
                      <Bar dataKey="clientes" radius={[7, 7, 0, 0]} maxBarSize={64}>
                        {data.porAnio.map((entry, i) => (
                          <Cell key={i} fill={entry.clientes === maxAnio ? "url(#gradCliAnioMax)" : "url(#gradCliAnio)"} />
                        ))}
                        <LabelList dataKey="clientes" position="top"
                          style={{ fontSize: "0.8rem", fontWeight: 800, fill: "#334155" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0",
                  padding: "20px 12px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                  <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#0f172a", padding: "0 8px 8px" }}>
                    Clientes activos por ramo
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(220, data.porRamo.length * 42)}>
                    <BarChart data={data.porRamo} layout="vertical" margin={{ top: 8, right: 36, left: 8, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                      <YAxis type="category" dataKey="ramo" width={110}
                        tick={{ fontSize: 11, fill: "#475569", fontWeight: 600 }} axisLine={false} tickLine={false} />
                      <Tooltip content={<ChartTooltip suffix=" activos" />} cursor={{ fill: `${COLOR}08` }} />
                      <Bar dataKey="clientes" radius={[0, 6, 6, 0]} maxBarSize={26}>
                        {data.porRamo.map((entry, i) => (
                          <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                        ))}
                        <LabelList dataKey="clientes" position="right"
                          style={{ fontSize: "0.74rem", fontWeight: 800, fill: "#334155" }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </>
        )}

      </div>
    </>
  );
}

export default ClientesActivos;
